import { gql } from '@apollo/client'
import {
  sendMetrics as sendMetricsStandalone,
  useDispatcher,
} from '@jusbrasil-web/shared-utils-events'
import React, { useCallback } from 'react'

import { UseChooseFrequencyLaterEvents_OfferLockCardFragment } from './__generated__/use-choose-frequency-later-events.graphql'

function normalizePlanName(planName: string): string {
  const withoutAccents = planName.normalize('NFD').replace(/\p{Diacritic}/gu, '')
  const lettersOnly = withoutAccents.replace(/[^a-zA-Z]/g, '')
  if (lettersOnly.length === 0) return ''
  return lettersOnly.charAt(0).toUpperCase() + lettersOnly.slice(1).toLowerCase()
}

type UseChooseFrequencyLaterEventsReturn = {
  metricsRef: React.RefObject<HTMLButtonElement> | undefined
  sendPlanSelectedEvent: (
    planName: string,
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => void
  sendMonthlyFrequencySelectedEvent: (
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => void
  sendYearlyFrequencySelectedEvent: (
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => void
  sendReturnToOffersEvent: (props: UseChooseFrequencyLaterEvents_OfferLockCardFragment) => void
  sendStep2ViewEvent: (props: UseChooseFrequencyLaterEvents_OfferLockCardFragment) => void
  sendModalClosedEvent: (
    currentStep: string,
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => void
}

export function useChooseFrequencyLaterEvents(): UseChooseFrequencyLaterEventsReturn {
  const { sendMetrics, ref: metricsRef } = useDispatcher<HTMLButtonElement>()

  const sendArgosEvent = useCallback(
    (eventName: string, props: UseChooseFrequencyLaterEvents_OfferLockCardFragment) => {
      const { experiment_id, feature, current_plan, next_plan, from_component, pid } =
        props.lockEventProperties
      sendMetrics(`Box.ChooseFrequencyLater${eventName}`, `${pid}`, {
        experiment_id,
        feature,
        current_plan,
        next_plan,
        from_component,
      })
    },
    [sendMetrics]
  )

  const sendPlanSelectedEvent = (
    planName: string,
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => {
    const normalizedPlanName = normalizePlanName(planName)
    sendArgosEvent(`${normalizedPlanName}PlanSelected`, props)
  }

  const sendMonthlyFrequencySelectedEvent = (
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => {
    sendArgosEvent('MonthlySelected', props)
  }

  const sendYearlyFrequencySelectedEvent = (
    props: UseChooseFrequencyLaterEvents_OfferLockCardFragment
  ) => {
    sendArgosEvent('YearlySelected', props)
  }

  const sendReturnToOffersEvent = (props: UseChooseFrequencyLaterEvents_OfferLockCardFragment) => {
    sendArgosEvent('BackToOffersClicked', props)
  }

  const sendStep2ViewEvent = (props: UseChooseFrequencyLaterEvents_OfferLockCardFragment) => {
    sendArgosEvent('Step2View', props)
  }

  const sendModalClosedEvent = useCallback(
    (currentStep: string, props: UseChooseFrequencyLaterEvents_OfferLockCardFragment) => {
      const { experiment_id, feature, current_plan, next_plan, from_component, pid } =
        props.lockEventProperties
      sendMetricsStandalone('Box.ChooseFrequencyLaterModalClosed', `${pid}`, {
        experiment_id,
        feature,
        current_plan,
        next_plan,
        from_component,
        current_step: currentStep,
      })
    },
    []
  )

  return {
    metricsRef,
    sendPlanSelectedEvent,
    sendMonthlyFrequencySelectedEvent,
    sendYearlyFrequencySelectedEvent,
    sendReturnToOffersEvent,
    sendStep2ViewEvent,
    sendModalClosedEvent,
  }
}

useChooseFrequencyLaterEvents.fragments = {
  offerLockCard: gql`
    fragment UseChooseFrequencyLaterEvents_offerLockCard on OfferLockCard {
      lockEventProperties {
        pid
        feature
        current_plan
        next_plan
        experiment_id
        plan_price
        modal_type
        app_id
        from_component
        resource_id
        parent_resource_id
        is_upgrade
        is_one_click
      }
    }
  `,
}
